import { motion } from "framer-motion";
import { Rocket, TrendingUp, DollarSign, Code, BarChart3, Zap, Heart, Users } from "lucide-react";

export default function About() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange/5">
      <div className="min-h-screen flex flex-col items-center justify-center p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="max-w-4xl w-full"
        >
          {/* Hero Section */}
          <div className="text-center mb-8 md:mb-12">
            <motion.h1 
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6 px-2"
            >
              Building the <span className="text-orange">Future of Hospitality Tech</span>
            </motion.h1>
            <p className="text-base md:text-xl text-gray-700 max-w-2xl mx-auto px-2">
              Where culinary passion meets cutting-edge technology
            </p>
          </div>

          {/* Our Story Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6 text-center">Our Story</h2>
            <div className="bg-white border border-gray-200 rounded-xl p-6 md:p-8 shadow-lg">
              <p className="text-gray-800 text-base md:text-lg mb-4 md:mb-6">
                Founded in 2025, <span className="font-bold text-orange">Morze Tech</span> emerged from a simple belief: technology should create meaningful human connections, not replace them.
              </p>
              <p className="text-gray-800 text-base md:text-lg mb-4 md:mb-6">
                Our flagship product, <a href="https://www.complimentstothechef.co.uk/" target="_blank" rel="noopener noreferrer" className="text-orange font-semibold hover:underline">Compliments to the Chef</a>, launched in early 2026 as a revolutionary platform that bridges the gap between diners and the talented chefs behind their favorite dishes. Now in our growth and validation phase, we're actively scaling our community and refining the product, a platform where every meal becomes a story and every chef gets the recognition they deserve.
              </p>
              <p className="text-gray-800 text-base md:text-lg">
                We're not just building an app; we're creating a movement that transforms how the culinary world connects, one dish at a time.
              </p>
            </div>
          </motion.div>

          {/* The Team Section - NEW */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6 text-center">The Bootstrap Journey</h2>
            <div className="bg-gradient-to-r from-orange/10 to-transparent border-l-4 border-orange rounded-r-xl p-6 md:p-8 shadow-lg">
              <p className="text-gray-800 text-base md:text-lg mb-4">
                We're a team of two committed founders who believe in building products that matter. We've dedicated ourselves to bringing <span className="font-semibold text-orange">Compliments to the Chef</span> to life.
              </p>
              <p className="text-gray-800 text-base md:text-lg mb-4">
                Bootstrapped and focused. No outside funding yet, just relentless execution, user feedback, and a shared vision for transforming the hospitality-tech space. We've shipped a fully functional MVP with features like chef portfolios, restaurant pages, and a chef network.
              </p>
              <p className="text-gray-800 text-base md:text-lg">
                This is the founder-led approach that defines us: build fast, learn faster, and stay laser-focused on the problem we're solving.
              </p>
            </div>
          </motion.div>

          {/* Current Phase */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6 text-center">Where We Are Now</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {[
                {
                  phase: "Phase 1",
                  title: "Soft Launch",
                  status: "COMPLETED",
                  desc: "Built our initial community with early adopters and gathered valuable feedback",
                  Icon: Rocket,
                  color: "text-gray-400"
                },
                {
                  phase: "Phase 2",
                  title: "Growth & Validation",
                  status: "IN PROGRESS",
                  desc: "Expanding our user base, refining features, and proving product-market fit",
                  Icon: TrendingUp,
                  color: "text-green-400"
                },
                {
                  phase: "Phase 3",
                  title: "Scale & Investment",
                  status: "NEXT",
                  desc: "Leveraging proven metrics to attract strategic investors and accelerate growth",
                  Icon: DollarSign,
                  color: "text-purple-400"
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -5 }}
                  className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <item.Icon className="w-8 h-8 md:w-10 md:h-10 mb-3 text-orange" />
                  <div className="text-xs text-gray-600 font-semibold mb-1">{item.phase}</div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <div className={`text-xs font-bold ${item.color} mb-3`}>{item.status}</div>
                  <p className="text-gray-700 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* What Makes Us Different */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">What Makes Us Different</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {[
                {
                  title: "Product-First Approach",
                  desc: "We don't just talk about ideas. We build and ship real products that solve real problems for chefs, diners, and restaurants.",
                  Icon: Code
                },
                {
                  title: "Data-Driven Decisions",
                  desc: "Every feature, every pivot is backed by user feedback and concrete metrics from real users",
                  Icon: BarChart3
                },
                {
                  title: "Rapid Iteration",
                  desc: "We move fast, learn faster, and continuously improve based on what our users tell us",
                  Icon: Zap
                },
                {
                  title: "Community-Centric",
                  desc: "Our users aren't just customers; they're partners in building the future of hospitality tech.",
                  Icon: Heart
                }
              ].map((value, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 shadow-lg hover:shadow-xl transition-all"
                >
                  <value.Icon className="w-8 h-8 mb-3 md:mb-4 text-orange" />
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">{value.title}</h3>
                  <p className="text-gray-700 text-sm md:text-base">{value.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Our Team */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mb-12 md:mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6 text-center">Our Founding Team</h2>
            <div className="bg-white border border-gray-200 rounded-xl p-6 md:p-8 shadow-lg">
              <p className="text-gray-800 text-base md:text-lg mb-6 text-center">
                Two founders united by a shared vision to transform the hospitality-tech industry
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    name: "Siddhant Sawant",
                    role: "Technical Director & Co-Founder",
                    focus: "Leading product development and technical architecture",
                    email: "siddhant@morzetech.com"
                  },
                  {
                    name: "Akshatha Poojari",
                    role: "Technical Director & Co-Founder",
                    focus: "Driving engineering excellence and platform scalability",
                    email: "akshatha.poojari01@gmail.com"
                  }
                ].map((founder, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ y: -3 }}
                    className="text-center p-4 rounded-lg hover:bg-orange/5 transition-colors"
                  >
                    <Users className="w-12 h-12 mb-3 mx-auto text-orange" />
                    <h3 className="text-base font-bold text-gray-900 mb-1">{founder.name}</h3>
                    <p className="text-xs text-orange font-semibold mb-2">{founder.role}</p>
                    <p className="text-xs text-gray-600 mb-2">{founder.focus}</p>
                    {founder.email && (
                      <a 
                        href={`mailto:${founder.email}`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {founder.email}
                      </a>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Vision Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="bg-gradient-to-r from-orange/20 to-transparent border border-orange/30 rounded-xl p-6 md:p-8 shadow-lg"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">Our Vision</h2>
            <p className="text-gray-800 text-base md:text-lg mb-3 md:mb-4">
              To become the world's leading platform connecting food lovers with culinary creators, transforming every dining experience into an opportunity for recognition, connection, and discovery.
            </p>
            <p className="text-gray-800 text-base md:text-lg">
              We're not just building a startup. We're building the infrastructure for a new era of hospitality where chefs are celebrated, diners are empowered, and restaurants thrive through authentic connections.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}